import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout from '../Guides/GuideLayout';
import nekrologPreview from '../../../img/guide-klepsydra-demo.webp';

const MUST_HAVE = [
    {
        title: 'Imię i nazwisko oraz daty',
        text: 'Pełne dane Zmarłego, często z nazwiskiem rodowym, oraz data śmierci i wiek. Pozwalają bez wątpliwości rozpoznać, kogo dotyczy nekrolog.',
    },
    {
        title: 'Termin i miejsce pogrzebu',
        text: 'Dzień, godzina i kościół, w którym zostanie odprawiona Msza św., oraz cmentarz. To najważniejsza informacja dla osób, które chcą pożegnać Zmarłego.',
    },
    {
        title: 'Różaniec lub czuwanie',
        text: 'Jeśli przed pogrzebem odbywa się modlitwa różańcowa – jej dzień, godzina i miejsce.',
    },
    {
        title: 'Informacja od rodziny',
        text: 'Najczęściej „O czym zawiadamia pogrążona w smutku rodzina” albo wymienienie najbliższych: żony, dzieci, wnuków.',
    },
    {
        title: 'Cytat lub sentencja (opcjonalnie)',
        text: 'Krótki werset z Pisma Świętego albo ulubione słowa Zmarłego nadają nekrologowi osobisty charakter.',
    },
];

const TIPS = [
    {
        title: 'Pisz prosto i godnie',
        text: 'Nekrolog to oficjalne zawiadomienie. Prosty, spokojny język sprawdza się lepiej niż rozbudowane opisy.',
    },
    {
        title: 'Sprawdź daty i miejsca',
        text: 'Błąd w godzinie albo nazwie kościoła może sprawić, że bliscy nie dotrą na pogrzeb. Zgadzaj datę z dniem tygodnia.',
    },
    {
        title: 'Dopasuj formę do miejsca publikacji',
        text: 'Nekrolog w gazecie, na tablicy ogłoszeń i w internecie różni się formatem. Na tablicę – duże imię i nazwisko, czytelne godziny.',
    },
    {
        title: 'Zadbaj o czytelność',
        text: 'Wyraźna czcionka, kontrast i przejrzysty układ sprawiają, że nekrolog można przeczytać z kilku metrów.',
    },
    {
        title: 'Skorzystaj z gotowego wzoru',
        text: 'Zamiast układać tekst od zera, wpisz dane do generatora – układ, czcionki i ramka dopasują się same.',
    },
];

const BENEFITS = [
    {
        title: 'Gotowe w kilka minut',
        text: 'Wpisujesz dane Zmarłego, a program układa tekst na wzorze i przygotowuje plik do druku.',
    },
    {
        title: 'Eleganckie szablony',
        text: 'Kilka wzorów nekrologów i klepsydr – także własne szablony zakładu z jego ramką i nazwą.',
    },
    {
        title: 'Kompletna treść',
        text: 'Formularz prowadzi przez wszystkie potrzebne informacje: daty, godziny, miejsca i rodzinę.',
    },
    {
        title: 'PDF lub JPG do druku',
        text: 'Plik w wysokiej rozdzielczości – do wydruku na miejscu, wysłania do drukarni albo publikacji w internecie.',
    },
];

const FAQ = [
    {
        question: 'Co to jest nekrolog?',
        answer: 'Nekrolog to zawiadomienie o śmierci bliskiej osoby z informacją o terminie i miejscu pogrzebu. Publikuje się go w prasie, w internecie albo wywiesza na tablicach ogłoszeń, w kościele i przy domu Zmarłego.',
    },
    {
        question: 'Czym różni się nekrolog od klepsydry?',
        answer: 'W praktyce te słowa często używane są zamiennie. Klepsydrą nazywa się zwykle drukowane zawiadomienie wywieszane na tablicach ogłoszeń i w kościele, a nekrologiem – także ogłoszenie w gazecie lub w internecie.',
    },
    {
        question: 'Kto przygotowuje nekrolog?',
        answer: 'Najczęściej zakład pogrzebowy na podstawie informacji od rodziny. Rodzina może też przygotować nekrolog samodzielnie, np. w darmowym demo generatora Nekrodruk.',
    },
    {
        question: 'Co napisać w nekrologu?',
        answer: 'Imię i nazwisko Zmarłego, datę śmierci i wiek, termin i miejsce Mszy św. oraz pogrzebu, ewentualnie różaniec, a na końcu informację od rodziny. Można dodać krótki cytat lub sentencję.',
    },
];

export default function CoToNekrolog() {
    return (
        <GuideLayout
            path='/co-to-nekrolog'
            title='Co to jest nekrolog? Jak napisać nekrolog – poradnik | Nekrodruk.pl'
            description='Nekrolog to zawiadomienie o śmierci i pogrzebie. Dowiedz się, co powinien zawierać nekrolog, jak go napisać, czym różni się od klepsydry i jak szybko przygotować go online.'
            keywords='co to nekrolog, jak napisać nekrolog, nekrolog wzór, co zawiera nekrolog, nekrolog a klepsydra, generator nekrologów, nekrolog online, nekrolog do druku'
            heading={<>Co to jest nekrolog<em> i jak go napisać?</em></>}
            lead='Nekrolog to zawiadomienie o śmierci i uroczystościach pogrzebowych – wywieszane na tablicach ogłoszeń, w kościele lub publikowane w prasie i internecie. Sprawdź, co powinien zawierać i jak przygotować go bez błędów.'
            actions={
                <>
                    <Link to='/demo' className='guide_btn guide_btn--light'>
                        Przygotuj nekrolog w demo
                    </Link>
                    <span className='guide_heroNote'>Bez logowania i bez instalowania programów</span>
                </>
            }
            faq={FAQ}
        >
            <section className='guide_section'>
                <div className='guide_inner'>
                    <span className='guide_kicker'>Definicja</span>
                    <h2 className='guide_title'>Skąd się wziął nekrolog?</h2>
                    <div className='guide_text'>
                        <p>
                            Słowo <em>nekrolog</em> pochodzi z greki: <em>nekros</em> (zmarły) i <em>logos</em> (słowo).
                            Dawniej był to krótki tekst wspomnieniowy o Zmarłym, dziś najczęściej ma formę graficzną:
                            z krzyżem, imieniem i nazwiskiem oraz informacją o pogrzebie.
                        </p>
                        <p>
                            W Polsce nekrolog jest mocno związany z tradycją – wywiesza się go na tablicach ogłoszeń, w
                            kościele i przy domu Zmarłego. Przygotowaniem zajmuje się zwykle <strong>zakład pogrzebowy</strong>{' '}
                            na podstawie informacji od rodziny. To ostatnie publiczne pożegnanie, dlatego powinno być
                            staranne i godne.
                        </p>
                    </div>
                </div>
            </section>

            <section className='guide_section guide_section--soft'>
                <div className='guide_inner'>
                    <h2 className='guide_title'>Co powinien zawierać nekrolog?</h2>
                    <p className='guide_lead'>
                        Bez tych informacji nekrolog nie spełni swojego zadania – ktoś może nie dotrzeć na Mszę albo pomylić
                        cmentarz.
                    </p>
                    <ul className='guide_checklist'>
                        {MUST_HAVE.map((item) => (
                            <li key={item.title}>
                                <strong>{item.title}</strong>
                                <span>{item.text}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className='guide_section'>
                <div className='guide_inner'>
                    <span className='guide_kicker'>Wskazówki</span>
                    <h2 className='guide_title'>Jak napisać dobry nekrolog?</h2>
                    <ol className='guide_steps guide_steps--wide'>
                        {TIPS.map((tip) => (
                            <li key={tip.title}>
                                <strong>{tip.title}</strong>
                                <span>{tip.text}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <section className='guide_section guide_section--soft'>
                <div className='guide_inner'>
                    <h2 className='guide_title'>Nekrolog a klepsydra</h2>
                    <div className='guide_text'>
                        <p>
                            Oba słowa często oznaczają to samo. <strong>Klepsydrą</strong> nazywa się zwykle drukowane
                            zawiadomienie wywieszane na tablicach ogłoszeń i w kościele, a <strong>nekrologiem</strong> – także
                            ogłoszenie w gazecie lub w internecie. Treść jest bardzo podobna: dane Zmarłego, termin i miejsce
                            pogrzebu oraz informacja od rodziny.
                        </p>
                        <p>
                            Gotowy wzór tekstu i listę obowiązkowych informacji znajdziesz w poradniku{' '}
                            <Link to='/klepsydra-wzor' className='guide_inlineLink'>klepsydra – wzór do edycji i druku</Link>.
                        </p>
                    </div>
                </div>
            </section>

            <section className='guide_section'>
                <div className='guide_inner'>
                    <div className='guide_showcase'>
                        <figure className='guide_preview'>
                            <img
                                src={nekrologPreview}
                                alt='Przykładowy nekrolog przygotowany w generatorze Nekrodruk'
                                width='960'
                                height='676'
                                loading='lazy'
                            />
                            <figcaption>Nekrolog przygotowany w demo Nekrodruku (wersja demo ma znak wodny)</figcaption>
                        </figure>
                        <div>
                            <span className='guide_kicker'>Nekrodruk</span>
                            <h2 className='guide_title'>Gotowy nekrolog w kilka minut</h2>
                            <div className='guide_cards guide_cards--compact'>
                                {BENEFITS.map((item) => (
                                    <article key={item.title} className='guide_card'>
                                        <h3>{item.title}</h3>
                                        <p>{item.text}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className='guide_section guide_section--soft'>
                <div className='guide_inner'>
                    <div className='guide_callout'>
                        <div>
                            <h2>
                                Wypróbuj generator nekrologów<em> za darmo</em>
                            </h2>
                            <p>
                                Bez zakładania konta i bez opłat. Zmień tekst na gotowym wzorze i pobierz nekrolog jako PDF
                                lub JPG.
                            </p>
                        </div>
                        <div className='guide_actions'>
                            <Link to='/demo' className='guide_btn guide_btn--light'>
                                Wypróbuj demo
                            </Link>
                            <Link to='/register' className='guide_btn guide_btn--ghost'>
                                Załóż konto
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </GuideLayout>
    );
}
